import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewsCard from "@/components/feature/NewsCard";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";

export default function NewsDetailPage() {
  const { id } = useParams();
  const news = newsList.find((n) => String(n.id) === id);
  const [copied, setCopied] = useState(false);

  usePageMeta({
    title: news ? `${news.title} - 大岛优香资讯` : "资讯详情 - 大岛优香资讯",
    description: news
      ? news.summary
      : "大岛优香资讯详情页，阅读完整报道内容与相关推荐。",
    keywords: news ? `大岛优香,${news.category},${news.title}` : "大岛优香,资讯详情",
  });

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  if (!news) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <span className="w-14 h-14 rounded-full bg-background-200 text-foreground-500 flex items-center justify-center mx-auto mb-4">
          <i className="ri-file-unknow-line text-2xl" />
        </span>
        <p className="font-heading font-bold text-foreground-800 mb-4">未找到这条资讯</p>
        <Link
          to="/news"
          className="inline-block px-5 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          返回资讯列表
        </Link>
      </div>
    );
  }

  const related = newsList
    .filter((n) => n.category === news.category && n.id !== news.id)
    .slice(0, 4);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: news.title, text: news.summary, url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      }
    } catch {
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
      } catch {
        setCopied(false);
      }
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6 flex-wrap">
        <Link to="/" className="hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <Link to="/news" className="hover:text-primary-600">
          明星资讯
        </Link>
        <i className="ri-arrow-right-s-line w-4 h-4 flex items-center justify-center" />
        <span className="text-foreground-800 line-clamp-1">{news.title}</span>
      </nav>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 md:gap-10">
        <article className="lg:col-span-2">
          <Link
            to={`/news?category=${encodeURIComponent(news.category)}`}
            className="inline-block px-3 py-1 rounded-full bg-primary-50 text-primary-700 text-xs font-medium mb-4 hover:bg-primary-100 transition-colors"
          >
            {news.category}
          </Link>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 leading-snug mb-4">
            {news.title}
          </h1>
          <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-500 pb-5 border-b border-background-200 mb-6">
            <span className="flex items-center gap-1">
              <i className="ri-user-line w-4 h-4 flex items-center justify-center" />
              {news.author}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line w-4 h-4 flex items-center justify-center" />
              {news.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line w-4 h-4 flex items-center justify-center" />
              {news.views} 阅读
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-links-line w-4 h-4 flex items-center justify-center" />
              {news.source}
            </span>
          </div>

          <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden bg-background-200 mb-6">
            <img
              src={news.image}
              alt={news.title}
              title={`${news.title} - 大岛优香资讯`}
              className="w-full h-full object-cover object-top"
            />
          </div>

          <div className="space-y-5">
            {news.content.map((para, i) => (
              <p key={i} className="text-[15px] md:text-base text-foreground-800 leading-8">
                {para}
              </p>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-3 mt-8 pt-6 border-t border-background-200">
            <span className="text-sm text-foreground-600">分享这条资讯：</span>
            <button
              type="button"
              onClick={handleShare}
              className="flex items-center gap-2 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center" />
              生成分享链接
            </button>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(window.location.href);
                setCopied(true);
              }}
              className="flex items-center gap-2 px-4 py-2 rounded-md border border-background-200 text-foreground-700 text-sm hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-links-line w-4 h-4 flex items-center justify-center" />
              复制链接
            </button>
            {copied && (
              <span className="text-sm text-primary-600 animate-fade-up">链接已复制，快去分享吧</span>
            )}
          </div>
        </article>

        <aside className="space-y-8">
          <div className="bg-background-100 border border-background-200 rounded-lg p-5">
            <h3 className="font-heading text-base font-bold text-foreground-950 mb-4">
              相关推荐
            </h3>
            {related.length > 0 ? (
              <ul className="space-y-4">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="flex gap-3 group cursor-pointer">
                      <div className="w-20 h-16 rounded-md overflow-hidden bg-background-200 shrink-0">
                        <img
                          src={item.image}
                          alt={item.title}
                          title={`${item.title} - 大岛优香资讯`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <span className="text-sm text-foreground-800 leading-snug line-clamp-3 group-hover:text-primary-600 transition-colors">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-foreground-500">该分类暂时没有更多相关资讯</p>
            )}
          </div>

          <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5">
            <h3 className="font-heading text-base font-bold text-foreground-950 mb-2">
              更多分类
            </h3>
            <div className="flex flex-wrap gap-2">
              {["最新动态", "娱乐新闻", "活动预告"].map((cat) => (
                <Link
                  key={cat}
                  to={`/news?category=${encodeURIComponent(cat)}`}
                  className="px-3 py-1.5 rounded-full bg-background-50 border border-secondary-200 text-sm text-foreground-700 hover:border-primary-300 transition-colors whitespace-nowrap"
                >
                  {cat}
                </Link>
              ))}
            </div>
          </div>
        </aside>
      </div>

      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-heading text-xl font-bold text-foreground-950 mb-5">
            同分类延伸阅读
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((item) => (
              <NewsCard key={item.id} news={item} compact />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}